import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";

const IMG_LEFT =
  "https://images.unsplash.com/photo-1609200660087-e23d23d7e958?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHxzcG9ydHMlMjB0cmFkaW5nJTIwY2FyZHN8ZW58MHx8fHwxNzg0MTM5Mzg0fDA&ixlib=rb-4.1.0&q=85";
const IMG_RIGHT =
  "https://images.unsplash.com/photo-1728393287642-13bee7126ae8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxjb3R0b24lMjB3YXNoY2xvdGhzJTIwaGFuZG1hZGV8ZW58MHx8fHwxNzg0MTM5Mzg0fDA&ixlib=rb-4.1.0&q=85";

const line = {
  initial: { y: "110%" },
  animate: (i = 0) => ({
    y: "0%",
    transition: {
      duration: 1.1,
      delay: 0.25 + i * 0.12,
      ease: [0.65, 0, 0.35, 1],
    },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yLeft = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const yRight = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#050505]"
      data-testid="hero-section"
    >
      {/* Split imagery */}
      <div className="absolute inset-0 grid grid-cols-2">
        <motion.div
          style={{ y: yLeft, scale: scaleImg }}
          className="relative h-full w-full"
        >
          <img
            src={IMG_LEFT}
            alt="Sports trading cards on dark surface"
            className="h-full w-full object-cover"
            draggable={false}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/60 via-[#050505]/30 to-[#050505]/80" />
        </motion.div>
        <motion.div
          style={{ y: yRight, scale: scaleImg }}
          className="relative h-full w-full"
        >
          <img
            src={IMG_RIGHT}
            alt="Handmade cotton washcloths"
            className="h-full w-full object-cover"
            draggable={false}
          />
          <div className="absolute inset-0 bg-gradient-to-l from-[#050505]/60 via-[#050505]/30 to-[#050505]/80" />
        </motion.div>
      </div>

      {/* Center vertical divider */}
      <div className="pointer-events-none absolute inset-y-0 left-1/2 w-px bg-[#262626]/60 hidden md:block" />

      {/* Content */}
      <motion.div
        style={{ y: textY }}
        className="relative z-10 min-h-[100svh] flex flex-col justify-between px-6 md:px-10 pt-28 pb-10 md:pt-32 md:pb-16 max-w-[1440px] mx-auto"
      >
        {/* Top meta row */}
        <div className="flex items-start justify-between font-mono-alt text-[11px] tracking-[0.28em] uppercase text-[#a3a3a3]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.8 }}
          >
            <span className="text-[#c48a64]">/ 001</span> — Est. Hamilton, ON
          </motion.div>
          <motion.div
            className="hidden md:block text-right"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            Two brands. <br /> One curated hub.
          </motion.div>
        </div>

        {/* Headline */}
        <div className="mt-auto">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15, duration: 0.9 }}
            className="font-mono-alt text-[11px] tracking-[0.3em] uppercase text-[#c48a64] mb-6 md:mb-8"
          >
            ◆ The Dual Hub — Collectors & Makers
          </motion.p>

          <h1
            className="font-display text-[#f5f5f5] text-[13vw] md:text-[10.5vw] lg:text-[9.2vw] leading-[0.92] font-semibold"
            data-testid="hero-headline"
            aria-label="Curated collectibles, handmade with hands."
          >
            <span className="reveal-mask">
              <motion.span
                variants={line}
                initial="initial"
                animate="animate"
                custom={0}
                className="block"
              >
                Curated
              </motion.span>
            </span>
            <span className="reveal-mask">
              <motion.span
                variants={line}
                initial="initial"
                animate="animate"
                custom={1}
                className="block"
              >
                collectibles<span className="text-[#c48a64]">,</span>
              </motion.span>
            </span>
            <span className="reveal-mask">
              <motion.span
                variants={line}
                initial="initial"
                animate="animate"
                custom={2}
                className="block italic tracking-[-0.03em]"
                style={{ fontWeight: 500 }}
              >
                handmade with hands.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9 }}
            className="mt-8 md:mt-10 max-w-xl text-[#a3a3a3] text-base md:text-lg leading-relaxed"
          >
            One curated shopfront for two brands: <span className="text-[#f5f5f5]">Card Crack Chronicles</span> — premium sports cards & TCG — and <span className="text-[#f5f5f5]">The Hammer Haul</span> — general resale, liquidation finds, diverse collectibles, and featured handmade home goods.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.9 }}
            className="mt-10 md:mt-12 flex flex-col sm:flex-row gap-4 sm:gap-5"
          >
            <a
              href="#card-crack"
              data-testid="hero-cta-cards"
              className="btn-copper group inline-flex items-center justify-between gap-6 px-6 py-4 text-[13px] font-mono-alt tracking-[0.18em] uppercase min-w-[280px]"
            >
              Shop Cards & Collectibles
              <ArrowUpRight
                size={18}
                className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
              />
            </a>
            <a
              href="#hammer-haul"
              data-testid="hero-cta-goods"
              className="btn-ghost group inline-flex items-center justify-between gap-6 px-6 py-4 text-[13px] font-mono-alt tracking-[0.18em] uppercase min-w-[280px]"
            >
              Shop General & Handmade Goods
              <ArrowDownRight
                size={18}
                className="transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1"
              />
            </a>
          </motion.div>
        </div>

        {/* Bottom scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="mt-14 flex items-end justify-between font-mono-alt text-[11px] tracking-[0.28em] uppercase text-[#6b6b6b]"
        >
          <span>Scroll</span>
          <span className="hidden md:inline">Card Crack ⟷ Hammer Haul</span>
          <span>© {new Date().getFullYear()}</span>
        </motion.div>
      </motion.div>
    </section>
  );
}
